/**
 * Utility functions for calculating and formatting data age/staleness
 */

export interface DataAgeResult {
  /** True only for data 30+ days old ("outdated"), not data merely past minAgeMinutes */
  isStale: boolean;
  /** Formatted data age string (e.g., "15m", "2h", "15d", "45d"), empty if under minAgeMinutes */
  dataAge: string;
}

/**
 * Calculate data age and staleness for tile values
 *
 * Logic:
 * - Data < minAgeMinutes old (default 10): No timestamp shown (too recent)
 * - Data minAgeMinutes and older: Show the age (e.g., "15m", "2h", "15d", "45d")
 * - Data 30+ days old: Same day count, and isStale is also true ("outdated")
 *
 * @param ageInSeconds Age of the data in seconds
 * @param options Configuration options
 * @returns Data age information including stale flag and formatted age string
 */
export function calculateDataAge(
  ageInSeconds: number,
  options: { minAgeMinutes?: number } = {}
): DataAgeResult {
  const { minAgeMinutes = 10 } = options;

  const ageInMinutes = Math.floor(ageInSeconds / 60);
  const ageInHours = Math.floor(ageInSeconds / 3600);
  const ageInDays = Math.floor(ageInSeconds / 86400);
  const thirtyDaysInSeconds = 30 * 24 * 60 * 60;

  const isStale = ageInSeconds >= thirtyDaysInSeconds;

  // Less than minimum age - not stale yet
  if (ageInMinutes < minAgeMinutes) {
    return {
      isStale: false,
      dataAge: ''
    };
  }

  // Between min and 60 minutes
  if (ageInMinutes <= 60) {
    return {
      isStale,
      dataAge: `${ageInMinutes}m`
    };
  }

  // Between 60 minutes and 24 hours
  if (ageInHours < 24) {
    return {
      isStale,
      dataAge: `${ageInHours}h`
    };
  }

  // 24 hours and older (isStale marks 30+ days)
  return {
    isStale,
    dataAge: `${ageInDays}d`
  };
}

/**
 * Convenience wrapper: takes an ISO-8601 timestamp string and returns staleness info.
 * Returns empty/non-stale result for missing or future timestamps.
 */
export function calculateDataAgeFromTimestamp(
  time: string | undefined,
  options: { minAgeMinutes?: number } = {},
): DataAgeResult {
  if (!time) return { dataAge: '', isStale: false };
  const ageInSeconds = Math.floor((Date.now() - new Date(time).getTime()) / 1000);
  if (ageInSeconds < 0) return { dataAge: '', isStale: false };
  return calculateDataAge(ageInSeconds, options);
}

export interface DataAgeStatus {
  ageSeconds: number;
  label: string;
  /** Past the stale threshold */
  isStale: boolean;
  /** 30+ days old */
  isOutdated: boolean;
}

/** Age, display label and both stale flags for an ISO-8601 timestamp */
export function getDataAgeStatus(
  time: string | undefined,
  options: { minAgeMinutes?: number } = {},
): DataAgeStatus {
  const ageSeconds = time ? Math.max(0, Math.floor((Date.now() - new Date(time).getTime()) / 1000)) : 0;
  const {dataAge: label, isStale: isOutdated} = calculateDataAge(ageSeconds, options);

  return {ageSeconds, label, isStale: label !== '', isOutdated};
}
